import { Eye, Glasses, AudioLines, Type, HeartHandshake, GraduationCap, Briefcase } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const AUDIENCES = [
  { icon: Eye, title: "Macular Degeneration", desc: "Read mail, labels, and screens again — without strain.", testid: "audience-card-macular-degeneration" },
  { icon: Glasses, title: "Low Vision", desc: "Large type, high contrast, and a voice that reads for you.", testid: "audience-card-low-vision" },
  { icon: AudioLines, title: "Blindness", desc: "Audio-first design that describes and reads the world aloud.", testid: "audience-card-blindness" },
  { icon: Type, title: "Dyslexia", desc: "Listen while text highlights in sync — reading without the fight.", testid: "audience-card-dyslexia" },
  { icon: HeartHandshake, title: "Seniors", desc: "Big buttons, simple voice commands, zero confusing menus.", testid: "audience-card-seniors" },
  { icon: GraduationCap, title: "Students", desc: "Turn textbooks and handouts into listenable, explainable lessons.", testid: "audience-card-students" },
  { icon: Briefcase, title: "Busy Professionals", desc: "Summarize documents, contracts, and reports on the go.", testid: "audience-card-professionals" },
];

export default function WhoItsFor() {
  return (
    <section id="who" className="relative bg-[#050F1F]/50 py-24 lg:py-32" aria-labelledby="who-heading">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <span id="who-heading" className="sr-only">Who VoiceLens AI is for</span>
        <SectionHeading
          align="center"
          eyebrow={{ index: "02", label: "Who It's For" }}
          title={<>Built for someone <span className="text-glow-gradient">like you.</span></>}
          sub="Different challenges. The same frustration: important information, just out of reach. VoiceLens closes that gap."
        />

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {AUDIENCES.map(({ icon: Icon, title, desc, testid }, i) => (
            <Reveal key={title} delay={(i % 4) * 0.08} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <article
                data-testid={testid}
                className="card-glow glass-panel group flex h-full flex-col items-start rounded-3xl p-7"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-[#00D4FF]/30 bg-[#00D4FF]/10 transition-transform duration-300 group-hover:scale-110">
                  <Icon className="h-6 w-6 text-[#00D4FF]" strokeWidth={2.2} aria-hidden="true" />
                </div>
                <h3 className="font-display text-lg font-bold text-white">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300 sm:text-base">{desc}</p>
              </article>
            </Reveal>
          ))}

          <Reveal delay={0.24}>
            <div className="flex h-full flex-col justify-center rounded-3xl border border-[#2563EB]/40 bg-gradient-to-br from-[#2563EB]/25 to-[#00D4FF]/10 p-7" data-testid="audience-card-everyone">
              <p className="font-display text-lg font-bold leading-snug text-white sm:text-xl">
                …and anyone who has ever squinted at fine print.
              </p>
              <p className="mt-2 text-sm text-slate-300">VoiceLens is for every reader.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
